import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useInvitePreview, useClaimInvite } from "@/hooks/useBringFriend";
import { useAuth } from "@/components/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Gift, Ticket, AlertCircle, Calendar } from "lucide-react";
import { capture } from "@/services/posthog";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const REASON_MESSAGES: Record<string, string> = {
  expired: "This invite link has expired.",
  revoked: "This invite link is no longer valid.",
  already_claimed: "This invite has already been claimed.",
  not_found: "We couldn't find this invite. Please check your link.",
  invalid_token: "The invite link appears to be invalid.",
  campaign_inactive: "This campaign is no longer active.",
  campaign_ended: "This campaign has ended.",
};

export default function FriendInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { data: preview, isLoading } = useInvitePreview(token);
  const claimMutation = useClaimInvite();
  const [claimed, setClaimed] = useState(false);
  const [claimedEventId, setClaimedEventId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  if (claimed) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
              <Ticket className="h-8 w-8 text-success" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-foreground">You're Going!</h1>
          <p className="text-muted-foreground">
            Your free ticket has been confirmed. Check your email for details.
          </p>
          {claimedEventId && (
            <Button
              className="w-full"
              size="lg"
              onClick={() => navigate(`/events/${claimedEventId}`)}
            >
              View Event Details
            </Button>
          )}
        </Card>
      </div>
    );
  }

  if (!preview?.valid) {
    const reason = preview?.reason || "invalid";
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 text-center space-y-4">
          <div className="flex justify-center">
            <AlertCircle className="h-12 w-12 text-destructive" />
          </div>
          <h1 className="text-xl font-bold text-foreground">
            Invite Unavailable
          </h1>
          <p className="text-muted-foreground">
            {REASON_MESSAGES[reason] || REASON_MESSAGES.invalid_token}
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate("/events")}
          >
            Browse Events
          </Button>
        </Card>
      </div>
    );
  }

  const eventDate = preview.event_start_time
    ? new Date(preview.event_start_time)
    : null;

  const handleClaim = async () => {
    try {
      const result = await claimMutation.mutateAsync(token!);
      capture("bring_friend_claimed", {
        event_id: result.event_id,
      });
      setClaimedEventId(result.event_id);
      // Send confirmation email (non-critical)
      try {
        await supabase.functions.invoke("send-ticket-confirmation", {
          body: { ticketId: result.ticket_id },
        });
      } catch {
        // non-critical
      }
      setClaimed(true);
    } catch (err: any) {
      toast({
        title: "Could not claim ticket",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const handleSignUp = () => {
    navigate(`/auth?action=sign-up&redirectTo=/invite/${token}`);
  };


  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Gift className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">You're Invited!</h1>
          {preview.inviter_first_name && (
            <p className="text-muted-foreground">
              {preview.inviter_first_name} invited you to join them at:
            </p>
          )}
        </div>

        <div className="bg-muted rounded-lg p-4 space-y-2">
          <h2 className="font-semibold text-foreground text-lg">
            {preview.event_title}
          </h2>
          {eventDate && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {eventDate.toLocaleDateString(undefined, {
                weekday: "long",
                month: "long",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
              })}
            </div>
          )}
        </div>

        <div className="bg-success/5 border border-success/20 rounded-lg p-3">
          <p className="text-sm font-medium text-success">
            Your ticket is free! 🎉
          </p>
        </div>

        {isAuthenticated ? (
          <Button
            className="w-full"
            size="lg"
            onClick={handleClaim}
            disabled={claimMutation.isPending}
          >
            {claimMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <Ticket className="h-5 w-5 mr-2" />
            )}
            Claim Free Ticket
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Create a free account to claim your ticket.
            </p>
            <Button className="w-full" size="lg" onClick={handleSignUp}>
              Sign Up to Claim
            </Button>
            <p className="text-xs text-muted-foreground">
              This free ticket is for people new to Nimi. If you already have a
              Nimi account, you can still join by booking a ticket as usual.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
